import { StripePaymentMethods } from '@app/components/Stripe/types';
import { ProcessedSearchResultItem } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { ObjectValues } from '@app/types';
import { Item } from '@app/types/monolith/cart';
import {
  AnalyticsATCContextProperty,
  Item as AnalyticsItem,
  AnalyticsItemListID,
  AnalyticsItemsListIDNameMap,
  AnalyticsPageType,
  AnalyticsPageTypeProperty,
  AnalyticsPaymentType,
  RbGtmClient,
} from '@redbubble/next-analytics/gtm';
import { ExpressPaymentType } from '@stripe/stripe-js';
import { round } from 'lodash';
import { InventoryItem } from '../types/shop-bff/types';
import { getDiscountValue, getItemValue } from './common';
import { getDiscountTypeAndAmount } from './getPriceComponent';
import searchUUIDStorage, { SearchUUIDStorageKeyId } from './searchUUIDStorage';

export const trackGA4AddToCartEvent = ({
  item,
  workId,
  pageType,
  context,
  analyticsComponentID,
  index,
  searchUUIDStorageKey,
}: {
  item: InventoryItem | ProcessedSearchResultItem;
  workId: string;
  pageType: AnalyticsPageTypeProperty;
  context: AnalyticsATCContextProperty;
  analyticsComponentID?: AnalyticsItemListID;
  index?: number;
  searchUUIDStorageKey?: SearchUUIDStorageKeyId;
}) => {
  const priceTypeAndAmount = getDiscountTypeAndAmount(
    item.volumeDiscount?.thresholds || [],
    item.price
  );

  if (!priceTypeAndAmount) return null;

  const { discountType, discountedAmount } = priceTypeAndAmount;
  const discountValue = getDiscountValue(item.price);

  RbGtmClient.track({
    event: 'add_to_cart',
    page_type: pageType,
    context,
    currency: item.price ? item.price.currency : '',
    ecommerce: {
      currency: item.price ? item.price.currency : '',
      items: [
        {
          discount: getDiscountValue(item.price),
          index: index ?? null,
          product_type_id: item.productTypeId,
          blank_item_id: item.blankItemId,
          work_id: workId,
          item_id: item.id,
          retargeting_id: item.marketingProductTypeId,
          inventory_item_id: item.id,
          item_list_id: analyticsComponentID || '',
          item_list_name: analyticsComponentID
            ? AnalyticsItemsListIDNameMap[analyticsComponentID]
            : '',
          price: item.price ? item.price.amount : null,
          quantity: 1,
          display_price: discountedAmount,
          discount_type: discountType,
          custom_discount: discountValue,
          ...(item.gaCode && { item_category5: item.gaCode }),
        },
      ],
      value: item.price ? getItemValue(item.price) : '',
    },
    search_query_id: searchUUIDStorageKey
      ? (searchUUIDStorage.get(searchUUIDStorageKey) ?? null)
      : null,
  });
};

type PageTypeToPaymentTypeMap = {
  [AnalyticsPageType.Cart]: Record<
    Extract<ExpressPaymentType, 'apple_pay' | 'google_pay' | 'paypal'>,
    ObjectValues<typeof AnalyticsPaymentType>
  >;
  [AnalyticsPageType.Checkout]: Record<
    ObjectValues<typeof StripePaymentMethods>,
    ObjectValues<typeof AnalyticsPaymentType>
  >;
  [AnalyticsPageType.ProductDetailsPage]: Record<
    Extract<ExpressPaymentType, 'apple_pay' | 'google_pay' | 'paypal'>,
    ObjectValues<typeof AnalyticsPaymentType>
  >;
};

const pageTypeToGAPaymentType: PageTypeToPaymentTypeMap = {
  [AnalyticsPageType.Cart]: {
    apple_pay: AnalyticsPaymentType.ApplePayExpress,
    google_pay: AnalyticsPaymentType.GooglePayExpress,
    paypal: AnalyticsPaymentType.PaypalExpress,
  },
  [AnalyticsPageType.Checkout]: {
    apple_pay: AnalyticsPaymentType.ApplePay,
    google_pay: AnalyticsPaymentType.GooglePay,
    card: AnalyticsPaymentType.Stripe,
    external_paypal: AnalyticsPaymentType.Paypal,
    klarna: AnalyticsPaymentType.Klarna,
    afterpay_clearpay: AnalyticsPaymentType.Afterpay,
  },
  [AnalyticsPageType.ProductDetailsPage]: {
    paypal: AnalyticsPaymentType.PayPalExpressCartTray,
    apple_pay: AnalyticsPaymentType.ApplePayExpressCartTray,
    google_pay: AnalyticsPaymentType.GooglePayExpressCartTray,
  },
};

export const trackGA4SelectPaymentType = ({
  paymentType,
  page,
}: {
  paymentType: string;
  page: AnalyticsPageTypeProperty;
}) => {
  const gaPaymentType = pageTypeToGAPaymentType[page][paymentType];

  RbGtmClient.track({
    event: 'select_payment_type',
    page_location: window.location.toString(),
    payment_type: gaPaymentType,
  });
};

export const trackGA4BeginCheckout = ({
  currency,
  items,
  value,
  couponCode,
}: {
  currency: string;
  items: (AnalyticsItem & { coupon: string })[];
  value: number;
  couponCode?: string | null;
}) => {
  RbGtmClient.track({
    event: 'begin_checkout',
    coupon: couponCode || '',
    ecommerce: {
      coupon: couponCode || '',
      currency,
      value: value.toString(),
      items,
      retargeting_ids: items.map((item) => item.retargeting_id).join(','),
    },
  });
};

export const trackGA4ViewCart = ({
  currency,
  items,
  value,
  couponCode,
  shipping,
}: {
  currency: string;
  items: AnalyticsItem[];
  value: number;
  couponCode?: string | null;
  shipping: number;
}) => {
  RbGtmClient.track({
    event: 'view_cart',
    page_location: window.location.toString(),
    currency,
    ecommerce: {
      coupon: couponCode || '',
      currency,
      value: value.toString(),
      items,
      shipping,
    },
  });
};

export const trackGA4ContinueToPayment = ({
  pageType,
  couponCode,
  currency,
  value,
  items,
  showTaxSeparately,
}: {
  pageType: AnalyticsPageTypeProperty;
  couponCode?: string | null;
  currency: string;
  items: Item[];
  value?: number;
  showTaxSeparately: boolean;
}) => {
  RbGtmClient.track({
    event: 'continue_to_payment',
    page_location: window.location.toString(),
    page_type: pageType,
    ecommerce: {
      coupon: couponCode || '',
      currency,
      value: value?.toString() || '',
      items: items.map((item, index) =>
        monolithItemToAnalyticsItem(item, currency, index, showTaxSeparately)
      ),
    },
  });
};

const getDiscount = (item: Item) =>
  round(item.undiscounted_total_price - item.total_price, 2);

const getGAGiftCertificateItemCode = (item: Item, currency: string) => {
  return `gift-certificate-${currency}-${item.total_price}`;
};

const getGAItemId = (item: Item, currency: string) => {
  if (item.is_digital) {
    return getGAGiftCertificateItemCode(item, currency);
  }

  return `${item.work_id}_${item.product_type_id}_${item.blank_item_id}`;
};

export const monolithItemToAnalyticsItem = (
  item: Item,
  currency: string,
  index: number | null,
  showTaxSeparately: boolean
): AnalyticsItem & { coupon: string } => {
  const {
    item_price,
    item_price_ex_tax,
    quantity,
    work_id,
    product_type_id,
    blank_item_id,
    marketing_product_type_id,
    ga_code,
  } = item;

  return {
    item_id: getGAItemId(item, currency),
    item_list_id: '',
    item_list_name: '',
    price: showTaxSeparately ? item_price_ex_tax : item_price,
    discount: getDiscount(item),
    quantity,
    work_id: work_id?.toString() || '',
    product_type_id: product_type_id?.toString() || '',
    blank_item_id: blank_item_id?.toString() || '',
    inventory_item_id: getGAItemId(item, currency),
    retargeting_id: marketing_product_type_id,
    index,
    coupon: '',
    ...(ga_code && { item_category5: ga_code }),
  };
};

const buildCartItemAnalyticsPayload = ({
  item,
  quantity,
  currency,
  showTaxSeparately,
}: {
  item: Item;
  quantity: number;
  currency: string;
  showTaxSeparately: boolean;
}) => {
  const unitPrice = showTaxSeparately
    ? item.item_price_ex_tax
    : item.item_price;
  const value = (quantity * unitPrice).toFixed(2);
  const analyticsItem = {
    ...monolithItemToAnalyticsItem(item, currency, null, showTaxSeparately),
    quantity,
  };

  return { value, analyticsItem };
};

export const trackGA4AddCartItem = ({
  item,
  quantity,
  currency,
  showTaxSeparately,
  pageType,
  context,
}: {
  item: Item;
  quantity: number;
  currency: string;
  showTaxSeparately: boolean;
  pageType: AnalyticsPageTypeProperty;
  context: string;
}) => {
  const { value, analyticsItem } = buildCartItemAnalyticsPayload({
    item,
    quantity,
    currency,
    showTaxSeparately,
  });

  RbGtmClient.track({
    event: 'add_to_cart',
    page_location: window.location.toString(),
    page_type: pageType,
    context,
    currency,
    ecommerce: {
      currency,
      value,
      items: [analyticsItem],
    },
    search_query_id: null,
  });
};

export const trackGA4RemoveCartItem = ({
  item,
  quantity,
  currency,
  showTaxSeparately,
  pageType,
  context,
}: {
  item: Item;
  quantity: number;
  currency: string;
  showTaxSeparately: boolean;
  pageType: AnalyticsPageTypeProperty;
  context: string;
}) => {
  const { value, analyticsItem } = buildCartItemAnalyticsPayload({
    item,
    quantity,
    currency,
    showTaxSeparately,
  });

  RbGtmClient.track({
    event: 'remove_from_cart',
    page_location: window.location.toString(),
    page_type: pageType,
    context,
    ecommerce: {
      currency,
      value,
      items: [analyticsItem],
    },
  });
};
